/**
 * CashTable — bảng nhỏ của dashboard Cashflows (FX / Market / Cash at banks /
 * Credit facilities / USD exposure) style theo design
 * `plan/report/erp-report-cash.jsx`: header forest chữ trắng, số IBM Plex Mono
 * tabular căn phải, CHỈ kẻ ngang (không zebra/không kẻ dọc), số âm ngoặc đỏ.
 * Tái dùng contract `PnLColumn`/`PnLRow` + `fmtNum` của dashboard PnL (data
 * giữ nguyên — chỉ đổi skin); KHÔNG sửa PnLTable chung kẻo lệch page PnL.
 */
import { Box, Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material'

import { fmtDate, fmtNum } from '../pnl/format'
import type { PnLColumn, PnLRow } from '../pnl/PnLTable'
import { FONT_MONO, FONT_SANS, RPT } from './reportTokens'

const HEAD_SX = {
  bgcolor: RPT.forest,
  color: '#fff',
  fontFamily: FONT_SANS,
  fontWeight: 600,
  fontSize: '0.66rem',
  letterSpacing: '0.02em',
  lineHeight: 1.2,
  py: 0.6,
  px: 1,
  border: 0,
  borderBottom: `1px solid ${RPT.forest}`,
  whiteSpace: 'nowrap',
} as const

const CELL_SX = {
  fontSize: '0.68rem',
  lineHeight: 1.3,
  py: 0.45,
  px: 1,
  border: 0,
  borderBottom: `1px solid ${RPT.grid}`,
  verticalAlign: 'top',
} as const

interface CashTableProps {
  columns: PnLColumn[]
  rows: PnLRow[]
  /** Height cố định (px) — bảng dài scroll dọc + sticky header. */
  height?: number
  /** Bảng ngắn stretch khít card (table height 100%, dư chia đều dòng). */
  fill?: boolean
}

export default function CashTable({ columns, rows, height, fill }: CashTableProps) {
  return (
    <Box
      sx={{
        overflow: 'auto',
        ...(height ? { height } : {}),
        ...(fill ? { height: '100%' } : {}),
        border: `1px solid ${RPT.border}`,
        borderRadius: '6px',
      }}
    >
      <Table
        size="small"
        stickyHeader={!!height}
        sx={{
          borderCollapse: 'separate',
          borderSpacing: 0,
          ...(fill ? { height: '100%' } : {}),
        }}
      >
        <TableHead>
          <TableRow>
            {columns.map((c) => (
              <TableCell
                key={c.key}
                align={c.align ?? (c.text ? 'left' : 'right')}
                sx={{
                  ...HEAD_SX,
                  ...(c.text ? {} : { fontFamily: FONT_MONO }),
                  ...(c.width ? { minWidth: c.width } : {}),
                }}
              >
                {c.label}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, ri) => (
            <TableRow key={ri}>
              {columns.map((c) => {
                const raw = row[c.key]
                // Mặc định cột đầu = text (label) — giữ convention PnLTable.
                const isText = c.text ?? c.key === columns[0].key
                let content: React.ReactNode = ''
                let neg = false
                if (c.date) {
                  content = fmtDate(raw)
                } else if (isText) {
                  content = raw == null ? '' : String(raw)
                } else if (raw != null && raw !== '') {
                  const r = fmtNum(Number(raw), {
                    currency: c.currency,
                    decimals: c.decimals,
                    signed: c.signed,
                  })
                  content = r.text
                  neg = r.neg
                }
                return (
                  <TableCell
                    key={c.key}
                    align={c.align ?? (isText ? 'left' : 'right')}
                    sx={{
                      ...CELL_SX,
                      whiteSpace: c.pre ? 'pre' : isText ? 'normal' : 'nowrap',
                      overflowWrap: isText && !c.pre ? 'anywhere' : undefined,
                      fontFamily: isText ? FONT_SANS : FONT_MONO,
                      fontVariantNumeric: isText ? undefined : 'tabular-nums',
                      fontWeight: row.isTotal ? 700 : isText ? 500 : 400,
                      color: neg ? RPT.rose : RPT.ink,
                      bgcolor: row.isTotal ? RPT.summaryBg : '#fff',
                      ...(row.isTotal ? { borderTop: `1px solid ${RPT.border}` } : {}),
                    }}
                  >
                    {content}
                  </TableCell>
                )
              })}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  )
}
